import { useEffect, useMemo, useState } from 'react';
import { analyticsApi, buildAnalyticsQuery, type AnalyticsFilters, type AnalyticsMeta } from '../../services/analytics';

export const DEFAULT_TIMEZONE = 'America/Fortaleza';

/** Guia de leitura do painel (calculo e leitura de cada card), publicado junto do prototipo. */
export const GUIA_METRICAS_URL = 'https://fd-analytics-apresentacao-v9-review.pages.dev/guia-metricas.pdf';

/** Filtros com que cada pagina abre; "Limpar filtros" volta para eles. */
export const DEFAULT_FILTERS: AnalyticsFilters = { period: 'monthToDate' };

/** Algum filtro diferente do padrao (periodo, plano, status, segmento ou faturamento). */
export function hasActiveFilters(f: AnalyticsFilters): boolean {
    return (
        f.period !== DEFAULT_FILTERS.period || !!f.from || !!f.to || !!f.planType || !!f.status || !!f.segmento || !!f.faturamento
    );
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Dia local de Fortaleza no formato YYYY-MM-DD, usado como limite do periodo personalizado. */
export function todayInZone(timeZone: string): string {
    return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

/** Periodo personalizado so consulta com as duas datas validas e em ordem. */
export function isQueryable(filters: AnalyticsFilters): boolean {
    if (filters.period !== 'custom') return true;
    const { from, to } = filters;
    return !!from && !!to && DAY_RE.test(from) && DAY_RE.test(to) && from <= to;
}

/** Opcoes de filtro e fuso. Se falhar, a pagina segue sem as listas de segmento e faturamento. */
export function useAnalyticsMeta() {
    const [meta, setMeta] = useState<AnalyticsMeta | null>(null);
    useEffect(() => {
        const controller = new AbortController();
        analyticsApi
            .meta(controller.signal)
            .then(setMeta)
            .catch((e) => {
                if (!controller.signal.aborted) console.error(e);
            });
        return () => controller.abort();
    }, []);
    const timeZone = meta?.timezone ?? DEFAULT_TIMEZONE;
    const today = useMemo(() => todayInZone(timeZone), [timeZone]);
    return { meta, timeZone, today };
}

interface Settled<T> {
    key: string;
    data: T | null;
    error: string | null;
}

/**
 * Busca um bloco do painel pelos filtros. Mantem o ultimo resultado na tela enquanto o
 * novo carrega e so consulta com o periodo valido. `refreshKey` muda no "tentar novamente".
 */
export function useAnalyticsResource<T>(
    name: string,
    fetcher: (filters: AnalyticsFilters, signal: AbortSignal) => Promise<T>,
    filters: AnalyticsFilters,
    refreshKey: number,
) {
    const [settled, setSettled] = useState<Settled<T> | null>(null);
    const requestKey = isQueryable(filters) ? `${name}:${buildAnalyticsQuery(filters)}#${refreshKey}` : null;

    useEffect(() => {
        if (!requestKey) return;
        const controller = new AbortController();
        fetcher(filters, controller.signal)
            .then((data) => setSettled({ key: requestKey, data, error: null }))
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setSettled({
                    key: requestKey,
                    data: null,
                    error: e instanceof Error ? e.message : 'Não foi possível carregar o painel.',
                });
            });
        return () => controller.abort();
        // requestKey resume nome, filtros e o contador de nova tentativa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const loading = requestKey !== null && settled?.key !== requestKey;
    const current = settled && settled.key === requestKey ? settled : null;
    const data = current ? current.data : (settled?.data ?? null);
    return { data, loading, error: current?.error ?? null };
}
